import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useToast } from "@/components/ui/use-toast";
import { displayDate } from "@/lib/helper";
import {
  useClearMembershipFacilityOccurrenceMutation,
  useSetMembershipFacilityOccurrenceMutation,
} from "@/services/membershipFacilityScheduleApi";
import { MembershipAvailabilityCalendarWindow } from "@/types/membership-facility-schedule";
import { getApiErrorMessage } from "@/utils/apiError";
import { formatTimeRange12, isValidHours, toHHMM } from "@/utils/availabilityUtils";

interface OccurrenceSheetProps {
  /** The day that was clicked on the calendar; null closes the drawer. */
  occurrence: MembershipAvailabilityCalendarWindow | null;
  onClose: () => void;
  /** Open the full form to change every day of this membership's availability. */
  onEditRule: (membershipPlanId: number) => void;
  /** Callback fired after successfully updating or restoring an occurrence */
  onUpdated?: () => void | Promise<void>;
  /** Read-only access: show the day but offer no changes. */
  readOnly?: boolean;
}

/**
 * One day of an availability window, like one occurrence of a schedule: give this day
 * different hours, remove it, or put it back to the rule. Other days are not affected.
 */
const OccurrenceSheet = ({ occurrence, onClose, onEditRule, onUpdated, readOnly = false }: OccurrenceSheetProps) => {
  const { toast } = useToast();
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const [setOccurrence, { isLoading: isSaving }] = useSetMembershipFacilityOccurrenceMutation();
  const [clearOccurrence, { isLoading: isRestoring }] = useClearMembershipFacilityOccurrenceMutation();
  const busy = isSaving || isRestoring;

  useEffect(() => {
    if (occurrence) {
      setStartTime(toHHMM(occurrence.start_time));
      setEndTime(toHHMM(occurrence.end_time));
    }
  }, [occurrence]);

  if (!occurrence) return null;

  const dayLabel = displayDate(occurrence.date);
  const ruleHours = formatTimeRange12(
    occurrence.rule_start_time ?? occurrence.start_time,
    occurrence.rule_end_time ?? occurrence.end_time
  );

  const run = async (action: () => Promise<unknown>, success: string, failure: string) => {
    try {
      await action();
      toast({ variant: "success", title: success });
      await onUpdated?.();
      onClose();
    } catch (error: unknown) {
      toast({ variant: "destructive", title: failure, description: getApiErrorMessage(error) });
    }
  };

  const saveHours = () => {
    if (!isValidHours(startTime, endTime)) {
      toast({
        variant: "destructive",
        title: "Invalid hours",
        description: "Start time must be before end time.",
      });
      return;
    }
    run(
      () =>
        setOccurrence({
          id: occurrence.id,
          date: occurrence.date,
          start_time: startTime,
          end_time: endTime,
        }).unwrap(),
      `Availability updated for ${dayLabel}.`,
      "Could not update this day"
    );
  };

  const removeDay = () =>
    run(
      () => setOccurrence({ id: occurrence.id, date: occurrence.date, is_cancelled: true }).unwrap(),
      `Availability removed for ${dayLabel}.`,
      "Could not remove this day"
    );

  const restoreDay = () =>
    run(
      () => clearOccurrence({ id: occurrence.id, date: occurrence.date }).unwrap(),
      `${dayLabel} follows the rule again.`,
      "Could not restore this day"
    );

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="!h-screen">
        <SheetHeader>
          <SheetTitle>Availability on {dayLabel}</SheetTitle>
        </SheetHeader>
        <Separator className="h-[1px] rounded-full my-2" />

        <div className="py-2 space-y-4">
          <div className="p-3.5 bg-muted/40 rounded-lg border space-y-1.5 text-sm">
            <p className="font-semibold text-foreground">
              {occurrence.membership_plan_name || `Membership #${occurrence.membership_plan_id}`} ·{" "}
              {occurrence.facility_name || `Facility #${occurrence.facility_id}`}
            </p>
            <p className="text-xs text-muted-foreground">
              {occurrence.name ? `${occurrence.name} · ` : ""}Rule hours: <strong>{ruleHours}</strong>
            </p>
            {occurrence.is_cancelled && (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                This day has been removed from the availability.
              </Badge>
            )}
            {occurrence.is_modified && !occurrence.is_cancelled && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                This day has its own custom hours.
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="occurrence-start">Start Time</Label>
              <Input
                id="occurrence-start"
                type="time"
                value={startTime}
                disabled={readOnly || busy}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="occurrence-end">End Time</Label>
              <Input
                id="occurrence-end"
                type="time"
                value={endTime}
                disabled={readOnly || busy}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Changes here apply to {dayLabel} only. Other days keep the rule.
          </p>

          {!readOnly && (
            <div className="space-y-3">
              <LoadingButton
                loading={isSaving}
                disabled={busy}
                className="bg-primary text-black font-semibold w-full"
                onClick={saveHours}
              >
                Save for this day
              </LoadingButton>
              <div className="flex gap-3">
                {!occurrence.is_cancelled && (
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-700 w-full"
                    disabled={busy}
                    onClick={removeDay}
                  >
                    Remove this day
                  </Button>
                )}
                {occurrence.is_modified && (
                  <Button variant="outline" className="w-full" disabled={busy} onClick={restoreDay}>
                    Restore to rule
                  </Button>
                )}
              </div>
              <Button
                variant="ghost"
                className="w-full"
                disabled={busy}
                onClick={() => {
                  onClose();
                  onEditRule(occurrence.membership_plan_id);
                }}
              >
                Edit all days
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default OccurrenceSheet;
